/** @typedef {import('./SubscriptionCard/Props').SharedProps} Props */
/** @typedef {import('../../App/shared/SubscriptionService/Subscription').default} Subscription */

import { subscriptionService } from '../../service-container';
import { translate } from '../../App/Internationalization';
import captureError from '../../utils/captureError';
import ExternalAnchorButton from '../ExternalAnchorButton';
import FormGrayMessages from '../Forms/Messages/FormGrayMessages';
import FormSuccessMessages from '../Forms/Messages/FormSuccessMessages';
import Loading from '../Loading';
import React from 'react';

const btnClass = 'button w-full p-3 text-ms-3';

/**
 * @typedef {'free' | 'starter' | 'standard' | 'professional'} ScheduledTierName
 */

/**
 * @typedef {Subscription['scheduledChange']} ScheduledChange
 */

/**
 * @param {Date} date
 * @returns {string}
 */
function formatScheduledDate(date) {
	return date.toLocaleDateString('en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
}

/**
 * @param {string} title
 * @returns {ScheduledTierName | null}
 */
function getScheduledTierName(title) {
	switch (title) {
		case 'free':
		case 'starter':
		case 'standard':
		case 'professional':
			return title;
		default:
			return null;
	}
}

/**
 * @param {Props} props
 * @returns	{JSX.Element}
 */

// eslint-disable-next-line complexity
function SubscribeButton(props) {
	const [isCallingApi, setIsCallingApi] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState(
		/** @type {string | null} */ (null)
	);
	const [successMessage, setSuccessMessage] = React.useState(
		/** @type {string | null} */ (null)
	);

	if (props.isGettingData || props.subscription === null || isCallingApi) {
		return (
			<div className={`${btnClass} pointer-events-none`}>
				<div className="-my-19 -scale-50">
					<Loading />
				</div>
			</div>
		);
	}

	const currentSubscription = props.subscription;
	const tier = props.tier.title;
	const targetTier = props.tier.tier;
	const url = props.paymentLinks?.[tier]?.url;
	const isGroupSubscription = props.checkoutRole === 'manager';
	const hasExistingSubscription = props.hasExistingSubscription ?? false;
	const localPlanChangeStatus = props.lastPlanChange?.status;
	const hasMatchedTierFromState =
		props.lastPlanChange?.targetTier === targetTier;
	const groupTierFloor = props.groupTierFloor ?? 0;
	const effectiveCurrentTierValue =
		props.effectiveTierValue ?? props.currentTierValue ?? 0;
	const scheduledChange = currentSubscription.scheduledChange ?? null;
	const isAgentGroupFlow =
		props.checkoutRole === 'agent' && groupTierFloor > 0;
	const isIncludedWithGroup =
		isAgentGroupFlow &&
		targetTier !== undefined &&
		targetTier < groupTierFloor;
	const isScheduledTarget = scheduledChange?.targetTier === targetTier;
	const isDisabledByGroupTier = isIncludedWithGroup;
	const isInitialGroupFreeTier =
		isGroupSubscription && !hasExistingSubscription && targetTier === 0;

	// Determine if this is the current plan
	const isCurrentTier =
		targetTier !== undefined &&
		(isInitialGroupFreeTier ||
			((isGroupSubscription ? hasExistingSubscription : true) &&
				targetTier === effectiveCurrentTierValue));

	// Determine label from local plan-change state after a successful API update.
	let buttonText = translate('subscription.plans.cta'); // Default "Subscribe" or similar
	const feedbackMessages = (
		<React.Fragment>
			<FormSuccessMessages
				messages={successMessage ? [successMessage] : null}
			/>
			<FormGrayMessages
				heading="info"
				messages={errorMessage ? [errorMessage] : null}
			/>
		</React.Fragment>
	);
	const scheduledDateMessage =
		isScheduledTarget && scheduledChange?.effectiveDate instanceof Date
			? translate(
					'subscription.plans.scheduled_for',
					formatScheduledDate(scheduledChange.effectiveDate)
			  )
			: null;

	if (isCurrentTier) {
		if (
			hasMatchedTierFromState &&
			(localPlanChangeStatus === 'upgraded' ||
				localPlanChangeStatus === 'downgraded')
		) {
			buttonText = translate(
				`subscription.plans.${localPlanChangeStatus}`
			);
		} else {
			buttonText = translate('subscription.plans.current_plan');
		}
	} else if (isScheduledTarget) {
		buttonText = translate('subscription.plans.downgrade_scheduled');
	} else if (isIncludedWithGroup) {
		buttonText = translate('subscription.plans.included_with_group');
	} else {
		const referenceTierValue = effectiveCurrentTierValue;
		const tierValue = targetTier ?? 0;

		if (tierValue > referenceTierValue) {
			buttonText = translate('subscription.plans.upgrade');
		} else if (tierValue < referenceTierValue) {
			buttonText = translate('subscription.plans.downgrade');
		}
	}

	if (isCurrentTier) {
		return (
			<React.Fragment>
				<button
					className={`${btnClass} !bg-theme-gray !text-theme-fg-light cursor-default`}
					disabled={true}
					type="button"
				>
					{buttonText}
				</button>
				{feedbackMessages}
			</React.Fragment>
		);
	}

	if (isDisabledByGroupTier) {
		return (
			<button
				className={`${btnClass} !bg-theme-gray !text-theme-fg-light cursor-default`}
				disabled={true}
				type="button"
			>
				{buttonText}
			</button>
		);
	}

	if (isScheduledTarget) {
		return (
			<div className="flex flex-col gap-2">
				<button
					className={`${btnClass} !bg-theme-gray !text-theme-fg-light cursor-default`}
					disabled={true}
					type="button"
				>
					{buttonText}
				</button>
				{scheduledDateMessage ? (
					<p className="text-ms-1 text-center text-theme-fg-light">
						{scheduledDateMessage}
					</p>
				) : null}
			</div>
		);
	}

	// eslint-disable-next-line complexity
	async function handlePaidSubscriptionClick() {
		if (targetTier === undefined || isCallingApi) {
			return;
		}

		setIsCallingApi(true);
		setErrorMessage(null);
		setSuccessMessage(null);

		try {
			if (
				isGroupSubscription &&
				hasExistingSubscription &&
				!props.groupId
			) {
				throw new Error(translate('global.error'));
			}

			const apiMessage =
				isGroupSubscription && hasExistingSubscription
					? await subscriptionService.callGroupSubscriptionAction(
							props.groupId,
							targetTier
					  )
					: await subscriptionService.callSubscriptionAction(
							targetTier
					  );
			const status =
				props.tier.pricePerMonth > currentSubscription.priceInCents
					? 'upgraded'
					: 'downgraded';
			const scheduledTierName = getScheduledTierName(tier);
			/** @type {ScheduledChange} */
			const scheduledChangePayload =
				status === 'downgraded' && scheduledTierName !== null
					? {
							effectiveDate: currentSubscription.expirationDate,
							isGroupCoveredTarget:
								isAgentGroupFlow &&
								targetTier === groupTierFloor,
							targetTier,
							targetTierName: scheduledTierName,
							type: 'downgrade',
					  }
					: null;
			setSuccessMessage(
				apiMessage ?? translate(`subscription.plans.${status}`)
			);
			props.onPlanChanged?.({
				priceInCents: props.tier.pricePerMonth,
				scheduledChange: scheduledChangePayload,
				status,
				targetTier,
			});
		} catch (error) {
			let message = translate('global.error');

			if (error instanceof Error && error.message) {
				message = error.message;
			}

			setErrorMessage(message);
			captureError(error);
		} finally {
			setIsCallingApi(false);
		}
	}

	if (
		(isGroupSubscription &&
			!hasExistingSubscription &&
			!isInitialGroupFreeTier) ||
		(!isGroupSubscription && currentSubscription.priceInCents === 0)
	) {
		return (
			<ExternalAnchorButton
				className={`${btnClass} !text-white`}
				href={url}
				target="_self"
			>
				{buttonText}
			</ExternalAnchorButton>
		);
	}

	return (
		<React.Fragment>
			<button
				className={`${btnClass} !bg-theme-purple !text-white cursor-pointer`}
				disabled={isCallingApi}
				onClick={handlePaidSubscriptionClick}
				type="button"
			>
				{buttonText}
			</button>
			{feedbackMessages}
		</React.Fragment>
	);
}

export default React.memo(SubscribeButton);
